import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

/** A finger has to travel this far sideways to turn to the next photo. */
const SWIPE_DISTANCE_PX = 50;

interface ImageSliderProps {
  files: Array<{ id: string; title?: string }>;
  fileUrls: { [key: string]: string };
  isOpen: boolean;
  initialIndex: number;
  onClose: () => void;
}

export function ImageSlider({
  files,
  fileUrls,
  isOpen,
  initialIndex,
  onClose,
}: ImageSliderProps) {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  // A swipe can be followed by a click on the backdrop, which closes the gallery; that one click is swallowed.
  const swipeJustEndedRef = useRef(false);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  // Keyboard navigation
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!files) return;

    if (event.key === "Escape") {
      onClose();
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      setCurrentIndex((prev) => (prev === 0 ? files.length - 1 : prev - 1));
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      setCurrentIndex((prev) => (prev === files.length - 1 ? 0 : prev + 1));
    }
  }, [files, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "unset";
      };
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !files || files.length === 0) return null;

  const goToPrevious = () => setCurrentIndex((prev) => (prev === 0 ? files.length - 1 : prev - 1));
  const goToNext = () => setCurrentIndex((prev) => (prev === files.length - 1 ? 0 : prev + 1));

  const handlePrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    goToPrevious();
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    goToNext();
  };

  // Only a one-finger swipe on an unzoomed page turns the photo; a pinch or a swipe on a zoomed photo is the browser's.
  const handleTouchStart = (e: React.TouchEvent) => {
    const isZoomed = (window.visualViewport?.scale ?? 1) > 1;
    touchStartRef.current = e.touches.length === 1 && !isZoomed ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : null;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start || files.length < 2) return;
    const dx = e.changedTouches[0].clientX - start.x;
    const dy = e.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) < SWIPE_DISTANCE_PX || Math.abs(dx) < Math.abs(dy)) return;
    swipeJustEndedRef.current = true;
    if (dx < 0) goToNext();
    else goToPrevious();
  };

  const handleBackdropClick = () => {
    if (swipeJustEndedRef.current) {
      swipeJustEndedRef.current = false;
      return;
    }
    onClose();
  };

  const handleThumbnailClick = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(index);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
      onClick={handleBackdropClick}
      onPointerDown={() => {
        swipeJustEndedRef.current = false;
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="relative w-full h-full flex items-center justify-center p-4">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300 z-10 bg-black bg-opacity-50 rounded-full p-2 transition-colors"
          aria-label={t("feed_close")}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Previous Button */}
        {files.length > 1 && (
          <button
            onClick={handlePrevious}
            className="absolute left-4 text-white hover:text-gray-300 z-10 bg-black bg-opacity-50 rounded-full p-3 transition-colors"
            aria-label={t("feed_previous")}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        )}

        {/* Next Button */}
        {files.length > 1 && (
          <button
            onClick={handleNext}
            className="absolute right-4 text-white hover:text-gray-300 z-10 bg-black bg-opacity-50 rounded-full p-3 transition-colors"
            aria-label={t("feed_next")}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        )}

        {/* Image */}
        <div
          className="max-w-7xl max-h-full flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          {fileUrls[files[currentIndex].id] ? (
            <img
              src={fileUrls[files[currentIndex].id]}
              alt={files[currentIndex].title || t("feed_post_image", { index: currentIndex + 1 })}
              className="max-w-full max-h-[90dvh] object-contain rounded-lg"
            />
          ) : (
            <div className="w-full h-96 bg-gray-800 rounded-lg flex items-center justify-center">
              <span className="text-gray-400">{t("feed_loading")}</span>
            </div>
          )}
        </div>

        {/* Image Counter */}
        {files.length > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white bg-black bg-opacity-50 rounded-full px-4 py-2 text-sm">
            {currentIndex + 1} / {files.length}
          </div>
        )}

        {/* Thumbnail Navigation. Scrolling it sideways must not turn the photo, so its touches stay out of the swipe. */}
        {files.length > 1 && (
          <div
            onTouchStart={(e) => e.stopPropagation()}
            className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex space-x-2 max-w-4xl overflow-x-auto px-4 max-md:hidden"
          >
            {files.map((file, index) => (
              <button
                key={file.id}
                onClick={(e) => handleThumbnailClick(index, e)}
                className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                  index === currentIndex
                    ? "border-white"
                    : "border-transparent opacity-60 hover:opacity-100"
                }`}
              >
                {fileUrls[file.id] ? (
                  <img
                    src={fileUrls[file.id]}
                    alt={t("feed_thumbnail", { index: index + 1 })}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-700"></div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
